import { useEffect, useState } from 'react'
import CustomModal from '../shared/CustomModal/CustomModal'
import ModalBody from '../shared/CustomModal/ModalBody'
import ModalFooter from '../shared/CustomModal/ModalFooter'
import ModalHeader from '../shared/CustomModal/ModalHeader'
import { MdAdd, MdDelete } from 'react-icons/md'
import { toast } from 'react-toastify'

const LegalDescriptionWrapper = ({ open, hasVisible, legals, onClose, onSave, emptyEntryGenerator }) => {
    
    const [localLegals, setLocalLegals] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const allShowing = hasVisible && localLegals.length && localLegals.every(ll => ll.visible)
    const allHiding = hasVisible && localLegals.every(ll => !ll.visible)
    
    const initLocalLegals = () => {
        const legalDescriptions = [...(legals || [])]
        
        if (!legalDescriptions.length)
            legalDescriptions.push(emptyEntryGenerator(0))
        
        setLocalLegals(legalDescriptions)
    }
    
    const showOrHideAll = () => {
        if (allShowing) {
            setLocalLegals(localLegals.map(ll => ({ ...ll, visible: false })))
        } else if (allHiding) {
            setLocalLegals(localLegals.map(ll => ({ ...ll, visible: true })))
        } else {
            setLocalLegals(localLegals.map(ll => ({ ...ll, visible: true })))
        }
    }
    
    const addLegal = () => {
        const lastOrder = Math.max(0, ...localLegals.map(ll => ll.order))
        
        setLocalLegals([...localLegals, emptyEntryGenerator(lastOrder + 1)])
    }
    
    const removeLegal = idx => {
        const copy = [...localLegals]
        
        copy.splice(idx, 1)
        if (!copy.length)
            copy.push(emptyEntryGenerator(0))
        
        setLocalLegals(copy)
    }
    
    const onChange = (idx, field, value) => {
        const copy = [...localLegals]
        const legal = { ...copy[idx], [field]: value }
        
        copy.splice(idx, 1, legal)
        
        setLocalLegals(copy)
    }
    
    const onKeyDown = event => {
        event.stopPropagation()
    }
    
    const handleSave = async () => {
        const filteredLegals = localLegals.filter(ll =>
            !!ll.original?.trim()
            || !!ll.summary?.trim())
        
        try {
            setIsSaving(true)
            
            await onSave(filteredLegals)
        } catch (err) {
            toast.error('Failed to save Legal Descriptions')
        } finally {
            setIsSaving(false)
        }
    }
    
    const handleClose = () => {
        initLocalLegals()
        onClose()
    }
    
    useEffect(() => { initLocalLegals() }, [legals])
    
    return (
        <CustomModal open={open} modalHandler={handleClose} className="max-w-[70vw] w-[70vw]">
            <ModalHeader>Legal Descriptions & Summaries</ModalHeader>
            <ModalBody>
                <div className="grid grid-cols-2 gap-4">
                    
                    {hasVisible && <div className="flex gap-2 items-center text-sm text-neutral">
                        <input
                            id="showHide"
                            type="checkbox"
                            checked={allShowing}
                            onChange={showOrHideAll} />
                        <label htmlFor="showHide">Show/Hide All</label>
                    </div>}
                    <div className={'text-right ' + (!hasVisible && 'col-span-2')}>
                        <button className="btn btn-accent btn-outline btn-xs" onClick={addLegal}>
                            <MdAdd /> Add Legal
                        </button>
                    </div>
                    
                    <div className="col-span-2 max-h-[80vh] overflow-auto p-2 -m-2">
                        
                        {localLegals.map((ll, idx) => <div key={idx} className="collapse bg-base-100 mb-3">
                            <input type="checkbox" className="w-auto min-h-12 ml-12 mr-16" defaultChecked />
                            <div className="collapse-title p-2 px-4 min-h-0">
                                <div className="flex items-center gap-4">
                                    <div className="flex flex-1 gap-2">
                                        {hasVisible && <input
                                            type="checkbox"
                                            className="my-1"
                                            checked={ll.visible || false}
                                            onChange={() => onChange(idx, 'visible', !ll.visible)} />}
                                        <label htmlFor={`ld-${idx}`}>Full Legal {idx + 1}</label>
                                    </div>
                                    <div className="flex flex-1 justify-between items-center">
                                        <label htmlFor={`lds-${idx}`}>Legal Summary {idx + 1}</label>
                                        <button className="btn btn-ghost btn-sm !p-2 hide-on-close"
                                            onClick={() => removeLegal(idx)}>
                                            <MdDelete />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="collapse-content">
                                <div className="flex flex gap-4">
                                    <div
                                        contentEditable
                                        suppressContentEditableWarning
                                        id={`ld-${idx}`}
                                        className="textarea flex-1 leading-5 bg-base-200 min-h-[120px]
                                            whitespace-pre-line"
                                        onKeyDown={onKeyDown}
                                        onBlur={ev => onChange(idx, 'original', ev.target.innerText)}>
                                        {ll.original}
                                    </div>
                                    <div
                                        contentEditable
                                        suppressContentEditableWarning
                                        id={`lds-${idx}`}
                                        className="textarea flex-1 leading-5 bg-base-200 min-h-[120px]
                                            whitespace-pre-line"
                                        onKeyDown={onKeyDown}
                                        onBlur={ev => onChange(idx, 'summary', ev.target.innerText)}>
                                        {ll.summary}
                                    </div>
                                </div>
                            </div>
                        </div>,
                        )}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-outline btn-primary" onClick={handleClose}>Close</button>
                <button className="btn btn-primary" disabled={isSaving} onClick={handleSave}>Save</button>
            </ModalFooter>
        </CustomModal>
    )
}

export default LegalDescriptionWrapper
